/**
 * Error Message Transformer
 *
 * Transform API and system errors into user-friendly messages
 *
 * @module lib/errors/errorTransformer
 */

import { ApiError, ErrorCategory } from './AppError';
import { categorizeError } from './errorCategories';

/**
 * Translation function type
 */
type TranslationFunction = (key: string, params?: Record<string, string | number>) => string;

/**
 * Transform an API error to a user-friendly message
 */
function transformApiError(error: ApiError, t: TranslationFunction): string {
  const statusCode = error.statusCode;

  if (!statusCode) {
    return t('errors.api.unknown');
  }

  // Map status codes to translation keys
  const statusCodeMap: Record<number, string> = {
    400: 'errors.api.badRequest',
    401: 'errors.auth.unauthorized',
    403: 'errors.auth.forbidden',
    404: 'errors.api.notFound',
    408: 'errors.api.timeout',
    409: 'errors.api.conflict',
    422: 'errors.api.validationFailed',
    429: 'errors.api.rateLimited',
    500: 'errors.api.serverError',
    502: 'errors.api.badGateway',
    503: 'errors.api.unavailable',
    504: 'errors.api.gatewayTimeout',
  };

  const translationKey = statusCodeMap[statusCode] || 'errors.api.unknown';
  return t(translationKey);
}

/**
 * Transform a network error to a user-friendly message
 */
function transformNetworkError(error: Error, t: TranslationFunction): string {
  const message = error.message.toLowerCase();

  // Check for specific network error types
  if (message.includes('failed to fetch')) {
    return t('errors.network.fetchFailed');
  }

  if (message.includes('network request failed')) {
    return t('errors.network.requestFailed');
  }

  if (message.includes('timeout') || message.includes('timed out')) {
    return t('errors.network.timeout');
  }

  return t('errors.network.generic');
}

/**
 * Transform a camera error to a user-friendly message
 */
function transformCameraError(error: Error, t: TranslationFunction): string {
  const message = error.message.toLowerCase();

  // NotFoundError - no camera device found
  if (message.includes('notfoundederror') || message.includes('not found')) {
    return t('errors.camera.notFound');
  }

  // NotAllowedError - user denied camera permission
  if (message.includes('notallowederror') || message.includes('permission denied')) {
    return t('errors.camera.permissionDenied');
  }

  // NotReadableError - camera is in use
  if (message.includes('notreadableerror') || message.includes('could not start')) {
    return t('errors.camera.inUse');
  }

  // OverconstrainedError - constraints cannot be satisfied
  if (message.includes('overconstrainederror')) {
    return t('errors.camera.constraintFailed');
  }

  return t('errors.camera.generic');
}

/**
 * Transform a validation error to a user-friendly message
 */
function transformValidationError(error: Error, field?: string, t?: TranslationFunction): string {
  if (!t) {
    return error.message;
  }

  const message = error.message.toLowerCase();

  // Common validation patterns
  if (message.includes('required')) {
    return field
      ? t('errors.validation.fieldRequired', { field })
      : t('errors.validation.required');
  }

  if (message.includes('invalid email')) {
    return t('errors.validation.emailInvalid');
  }

  if (message.includes('too short')) {
    return field
      ? t('errors.validation.fieldTooShort', { field })
      : t('errors.validation.tooShort');
  }

  if (message.includes('too long')) {
    return field ? t('errors.validation.fieldTooLong', { field }) : t('errors.validation.tooLong');
  }

  // Return original message as fallback
  return error.message;
}

/**
 * Main error transformer function
 *
 * Automatically detects error type and transforms to user-friendly message
 */
export function transformError(error: unknown, t: TranslationFunction): string {
  // If it's an API error
  if (error instanceof ApiError) {
    return transformApiError(error, t);
  }

  // If it's a standard Error, categorize and transform
  if (error instanceof Error) {
    const category = categorizeError(error);

    switch (category) {
      case ErrorCategory.NETWORK_ERROR:
        return transformNetworkError(error, t);

      case ErrorCategory.TIMEOUT_ERROR:
        return t('errors.network.timeout');

      case ErrorCategory.AUTH_ERROR:
        return t('errors.auth.authenticationFailed');

      case ErrorCategory.PERMISSION_ERROR:
        return t('errors.auth.permissionDenied');

      case ErrorCategory.SESSION_ERROR:
        return t('errors.auth.sessionExpired');

      case ErrorCategory.VALIDATION_ERROR:
        return transformValidationError(error, undefined, t);

      case ErrorCategory.CAMERA_ERROR:
        return transformCameraError(error, t);

      case ErrorCategory.PARSE_ERROR:
        return t('errors.data.parseFailed');

      case ErrorCategory.DATA_ERROR:
        return t('errors.data.processingFailed');

      default:
        return t('errors.unknown');
    }
  }

  // If it's a string
  if (typeof error === 'string') {
    return error;
  }

  // Unknown error type
  return t('errors.unknown');
}

/**
 * Get actionable text for an error (what the user should do)
 */
export function getErrorAction(error: unknown, t: TranslationFunction): string | null {
  if (error instanceof ApiError) {
    const statusCode = error.statusCode;

    if (statusCode === 401) {
      return t('errors.actions.pleaseLogin');
    }

    if (statusCode === 403) {
      return t('errors.actions.contactAdmin');
    }

    if (statusCode === 404) {
      return t('errors.actions.checkUrl');
    }

    if (statusCode === 429) {
      return t('errors.actions.waitAndRetry');
    }

    if (statusCode && statusCode >= 500) {
      return t('errors.actions.tryAgainLater');
    }
  }

  if (error instanceof Error) {
    const category = categorizeError(error);

    if (category === ErrorCategory.NETWORK_ERROR) {
      return t('errors.actions.checkConnection');
    }

    if (category === ErrorCategory.TIMEOUT_ERROR) {
      return t('errors.actions.retry');
    }

    if (category === ErrorCategory.CAMERA_ERROR) {
      return t('errors.actions.checkCameraPermission');
    }
  }

  return t('errors.actions.retry');
}
